/**
 * Overview tab — the existing Calibration Planning cockpit (KPI strip, 24-month
 * load curve, onsite trips, ship/lab weekly queue, re-pitch). Relocated verbatim
 * from CalibrationPlanningPage during the #991 tab refactor; the Expired KPI tile
 * now switches to the Expired tab instead of opening the slide-over panel.
 */

import {
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  HelpCircle,
  MapPin,
  Truck,
  PhoneCall,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { MetricCard } from '@/components/MetricCard';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import BarChart from '@/components/charts/BarChart';
import { CHART_COLORS } from '@/components/charts/types';
import { FunnelBreakdown } from '../FunnelBreakdown';
import type { CalibrationPlanningResponse } from '../../types';

function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: columns }).map((_, c) => (
            <TableCell key={c}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

const loadCurveSeries = [
  { dataKey: 'onsite', name: 'Onsite', color: CHART_COLORS.danger },
  { dataKey: 'ds18b20', name: 'DS18B20', color: CHART_COLORS.primary },
  { dataKey: 'mail', name: 'Mail', color: CHART_COLORS.success },
  { dataKey: 'review', name: 'Review', color: CHART_COLORS.warning },
];

interface OverviewTabProps {
  data: CalibrationPlanningResponse | undefined;
  isPending: boolean;
  onOpenExpired: () => void;
}

export function OverviewTab({ data, isPending, onOpenExpired }: OverviewTabProps) {
  const loadCurveData = (data?.loadCurve ?? []).map((row) => ({
    name: row.month,
    onsite: row.onsite,
    ds18b20: row.ds18b20,
    mail: row.mail,
    review: row.review,
  }));

  return (
    <div className="space-y-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <MetricCard
          title="In Plan"
          value={data?.kpis?.inPlan ?? 0}
          icon={<CheckCircle2 />}
          color="blue"
          loading={isPending}
          tooltip="Probes scheduled within the current planning horizon."
        />
        <MetricCard
          title="Covered"
          value={data?.kpis?.covered ?? 0}
          icon={<CheckCircle2 />}
          color="green"
          loading={isPending}
          tooltip="Probes fully covered by an active calibration contract."
        />
        <MetricCard
          title="Partial"
          value={data?.kpis?.partial ?? 0}
          icon={<HelpCircle />}
          color="orange"
          loading={isPending}
          tooltip="Probes with partial contract coverage — needs review."
        />
        <MetricCard
          title="Verify"
          value={data?.kpis?.verify ?? 0}
          icon={<HelpCircle />}
          color="purple"
          loading={isPending}
          tooltip="Probes requiring manual verification before scheduling."
        />
        {/* Expired — switches to the Expired tab (the tile number equals the
            number of rows in that tab's table). */}
        <button
          type="button"
          onClick={onOpenExpired}
          disabled={isPending || !data}
          className="block w-full text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
          aria-label="View expired calibrations"
        >
          <MetricCard
            title="Expired"
            value={data?.kpis?.expired ?? 0}
            icon={<AlertTriangle />}
            color="red"
            loading={isPending}
          />
        </button>
        <MetricCard
          title="Re-pitch"
          value={data?.kpis?.repitch ?? 0}
          icon={<TrendingUp />}
          color="orange"
          loading={isPending}
        />
        <MetricCard
          title="Lab demand"
          value={data?.labCapacity?.demandThisWeek ?? 0}
          icon={<BarChart3 />}
          color="blue"
          suffix={data?.labCapacity ? `/ ${data.labCapacity.perWeek} u/wk` : undefined}
          tooltip={
            data?.labCapacity && (data.labCapacity.perWeek ?? 0) > 0
              ? `${data.labCapacity.utilizationPct ?? 0}% utilized`
              : undefined
          }
          loading={isPending}
        />
      </div>

      {/* Funnel — how the Expired number is derived */}
      {data?.funnel && data.funnel.length > 0 && (
        <FunnelBreakdown funnel={data.funnel} offFilter={data.offFilter} />
      )}

      {/* 24-month load curve */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            24-Month Load Curve
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isPending ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <BarChart
              data={loadCurveData}
              series={loadCurveSeries}
              xAxisKey="name"
              stacked
              showLegend
              height={300}
            />
          )}
        </CardContent>
      </Card>

      {/* Onsite trips */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Onsite Trips to Book
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Region</TableHead>
                  <TableHead scope="col" className="text-right">Probes</TableHead>
                  <TableHead scope="col" className="text-right">Trips</TableHead>
                  <TableHead scope="col">Earliest</TableHead>
                  <TableHead scope="col">Peak</TableHead>
                  <TableHead scope="col">Flag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending ? (
                  <TableSkeleton rows={5} columns={6} />
                ) : !data?.onsiteTrips?.length ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No onsite trips planned for this horizon.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.onsiteTrips.map((trip, idx) => (
                    <TableRow key={`${trip.region}-${idx}`}>
                      <TableCell className="font-medium">{trip.region || '(unknown region)'}</TableCell>
                      <TableCell className="text-right tabular-nums">{trip.probes.toLocaleString()}</TableCell>
                      <TableCell className="text-right tabular-nums">{trip.tripsNeeded}</TableCell>
                      <TableCell>{trip.earliest}</TableCell>
                      <TableCell>{trip.peakMonth}</TableCell>
                      <TableCell>{trip.flyout && <Badge variant="warning">Fly-out</Badge>}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Ship / lab weekly queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-4 w-4" />
            Ship / Lab Weekly Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Week</TableHead>
                  <TableHead scope="col" className="text-right">Units</TableHead>
                  <TableHead scope="col" className="text-right">Batches</TableHead>
                  <TableHead scope="col" className="text-right">Lab Days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending ? (
                  <TableSkeleton rows={6} columns={4} />
                ) : !data?.shipQueue?.length ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No ship queue data available.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.shipQueue.map((row) => (
                    <TableRow key={row.week}>
                      <TableCell>{row.week}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.units.toLocaleString()}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.batches}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.labDays}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Re-pitch list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4" />
            Re-Pitch Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Account</TableHead>
                  <TableHead scope="col" className="text-right">Probes</TableHead>
                  <TableHead scope="col">Flag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending ? (
                  <TableSkeleton rows={5} columns={3} />
                ) : !data?.repitch?.length ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      No accounts to re-pitch.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.repitch.map((row) => (
                    <TableRow key={row.account}>
                      <TableCell className="font-medium">{row.account}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.probes.toLocaleString()}</TableCell>
                      <TableCell>{row.flag && <Badge variant="secondary">{row.flag}</Badge>}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
